import { Component, Vue } from 'vue-property-decorator';
import axios from 'axios';
import { email, helpers, maxLength, minLength, required, numeric, requiredIf } from 'vuelidate/lib/validators';
import * as config from '@/shared/config/config';
import { utils } from '@/shared/utils';


const store = config.initVueXStore(Vue);
const loginPattern = helpers.regex('alpha', /^[a-zA-Z0-9!$&*+=?^_`{|}~.-]+@[a-zA-Z0-9-]+(?:\\.[a-zA-Z0-9-]+)*$|^[_.@A-Za-z0-9-]+$/);
const firstName = {
  required,
  minLength: minLength(1),
  maxLength: maxLength(50),
};

const lastName = {
  required,
  minLength: minLength(1),
  maxLength: maxLength(50),
};

const userEmail = {
  required,
  email,
  minLength: minLength(5),
  maxLength: maxLength(128),
  isUnique(value) {
    // standalone validator ideally should not assume a field is required
    if (value == null || value === '' || value.length < 5) {
      return true;
    }


    return new Promise<any>((resolve, reject) => {
      axios
      .post('api/public/users/exists', value,{ headers: {'Content-Type': 'text/plain' } } )
      .then((response) => {
        resolve( response.data === false);
      }).catch(() => {
        reject();
      });
    });
  }
};

const organizationName = {
  required,
  minLength: minLength(1),
  maxLength: maxLength(100),
  isUnique(value) {
    // standalone validator ideally should not assume a field is required
    if (value == null || value === '') {
      return true;
    }

    return new Promise<any>((resolve, reject) => {
      axios
      .post('api/public/organizations/exists', value,{ headers: {'Content-Type': 'text/plain' } } )
      .then((response) => {
        resolve(response.data === false);
      }).catch(() => {
        reject();
      });
    });
  }
};

const organizationNameNonUnique = {
  required,
  minLength: minLength(1),
  maxLength: maxLength(100)
};


const validations = {
  trialUser: {
    firstName,
    lastName,
    email: userEmail,
  },
  registerBuy: {
    firstName,
    lastName,
    email: userEmail,
    organizationName: organizationNameNonUnique
  },
  registerAccount: {
    firstName,
    lastName,
    email: userEmail,
    organizationName: organizationNameNonUnique,
    phoneNumber: {
      required,
      minLength: minLength(6),
      maxLength: maxLength(20),
    },
    country: {
      required
    },
    state: {
      required: requiredIf(function() {
         return this.registerAccount && (this.registerAccount.country === 'us' || this.registerAccount.country === 'au');
      }),
    },
    password: {
      required,
      minLength: minLength(6),
      maxLength: maxLength(60),
    },
  },
  settingsAccount: {
    firstName,
    lastName,
    userLogin: {
      required,
      minLength: minLength(1),
      maxLength: maxLength(128),
      pattern: loginPattern,
    },
    email: userEmail,
    organizationName
  },
  avatar: {
    avatarFile: {
      required
    }
  },
  organization: {
    name: organizationName,
    description: {},
    instanceName: {
      required
    },
    ldOffline: {},
    notes:{},
    type: {
    },
    taxStatus: {}
  },
  elbSubscription: {
    quantity: {
      required,
      numeric,
    },
    currentTermStart: {
    },
    currentTermEnd: {
    },
    plan: {
      required
    },
    nextBillingAt: {},
    trialStart: {},
    trialEnd: {},
    instanceName: {}
  },
  applicationUser: {
    firstName,
    lastName,
    email: userEmail,
    role: {
      required
    },
    metadata: {
    },
  },
  resellerResource: {
    greetings: {
      required
    },
    text1: {
      required
    },
    text2: {
      required
    },
    button: {
      required
    },
    // title: {
    //   required
    // }
  }
};


@Component({
  validations
})
export default class ValidationMixin extends Vue {

  // public settingsAccount: IApplicationUser = {};

  public firstNameErrors(v) {
    const errors = [];
    if (!v.firstName.$dirty) {
      return errors;
    }
    !v.firstName.minLength && errors.push(this.$t('settings.messages.validate.firstname.minlength'));
    !v.firstName.maxLength && errors.push(this.$t('settings.messages.validate.firstname.maxlength'));
    !v.firstName.required && errors.push(this.$t('settings.messages.validate.firstname.required'));

    return errors;
  }

  public lastNameErrors(v) {
    const errors = [];
    if (!v.lastName.$dirty) {
      return errors;
    }
    !v.lastName.minLength && errors.push(this.$t('settings.messages.validate.lastname.minlength'));
    !v.lastName.maxLength && errors.push(this.$t('settings.messages.validate.lastname.maxlength'));
    !v.lastName.required && errors.push(this.$t('settings.messages.validate.lastname.required'));

    return errors;
  }

  public emailErrors(v) {
    const errors = [];
    if (!v.email.$dirty) {
      return errors;
    }
    !v.email.email && errors.push(this.$t('global.messages.validate.email.invalid'));
    !v.email.minLength && errors.push(this.$t('global.messages.validate.email.minlength'));
    !v.email.maxLength && errors.push(this.$t('global.messages.validate.email.maxlength'));
    !v.email.required && errors.push(this.$t('global.messages.validate.email.required'));
    !v.email.isUnique && errors.push('email is already in use');

    return errors;
  }

  public usernameErrors(v) {
    const errors = [];
    if (!v.userLogin.$dirty) {
      return errors;
    }
    !v.userLogin.pattern && errors.push(this.$t('register.messages.validate.login.pattern'));
    !v.userLogin.minLength && errors.push(this.$t('register.messages.validate.login.minlength'));
    !v.userLogin.maxLength && errors.push(this.$t('register.messages.validate.login.maxlength'));
    !v.userLogin.required && errors.push(this.$t('register.messages.validate.login.required'));

    return errors;
  }

  public userTypeErrors() {
    const errors = [];
    if (!this.$v.applicationUser.role.$dirty)  {
      return errors;
    }
    !this.$v.applicationUser.role.required && errors.push('User Type is required');

    return errors;
  }

  public organizationNameErrors(v, field) {
    const errors = [];
    if (!v[field].$dirty) {
      return errors;
    }
    !v[field].minLength && errors.push(this.$t('settings.messages.validate.organaizationname.minlength'));
    !v[field].maxLength && errors.push(this.$t('settings.messages.validate.organaizationname.maxlength'));
    !v[field].required && errors.push(this.$t('settings.messages.validate.organaizationname.required'));
    !v[field].isUnique && errors.push('company name is taken, please choose another name');

    return errors;
  }

  public organizationNameErrorsNonUnique(v, field) {
    const errors = [];
    if (!v[field].$dirty) {
      return errors;
    }
    !v[field].minLength && errors.push(this.$t('settings.messages.validate.organaizationname.minlength'));
    !v[field].maxLength && errors.push(this.$t('settings.messages.validate.organaizationname.maxlength'));
    !v[field].required && errors.push(this.$t('settings.messages.validate.organaizationname.required'));

    return errors;
  }

  public regionErrors(v) {
    const errors = [];
    if (!v.instanceName.$dirty) {
      return errors;
    }
    !v.instanceName.required && errors.push(this.$t('entity.validation.required'));

    return errors;
  }

  public quantityErrors() {
    const errors = [];
    if (!this.$v.elbSubscription.quantity.$dirty) {
      return errors;
    }
    !this.$v.elbSubscription.quantity.numeric && errors.push(this.$t('entity.validation.number'));
    !this.$v.elbSubscription.quantity.required && errors.push(this.$t('entity.validation.required'));

    return errors;
  }

  public planErrors() {
    const errors = [];
    if (!this.$v.elbSubscription.plan.$dirty) {
      return errors;
    }
    !this.$v.elbSubscription.plan.required && errors.push(this.$t('entity.validation.required'));

    return errors;
  }

  public phoneNumberErrors() {
    const errors = [];
    if (!this.$v.registerAccount.phoneNumber.$dirty) {
      return errors;
    }
    !this.$v.registerAccount.phoneNumber.minLength && errors.push(this.$t('register.messages.validate.phone.minlength'));
    !this.$v.registerAccount.phoneNumber.maxLength && errors.push(this.$t('register.messages.validate.phone.maxlength'));
    !this.$v.registerAccount.phoneNumber.required && errors.push(this.$t('register.messages.validate.phone.required'));

    return errors;
  }

  public countryErrors() {
    const errors = [];
    if (!this.$v.registerAccount.country.$dirty) {
      return errors;
    }
    !this.$v.registerAccount.country.required && errors.push(this.$t('register.messages.validate.country.required'));

    return errors;
  }

  public stateErrors() {
    const errors = [];
    if (!this.$v.registerAccount.state.$dirty) {
      return errors;
    }
    !this.$v.registerAccount.state.required && errors.push(this.$t('register.messages.validate.state.required'));

    return errors;
  }


  public passwordErrors() {
    const errors = [];
    if (!this.$v.registerAccount.password.$dirty) {
      return errors;
    }
    !this.$v.registerAccount.password.required && errors.push(this.$t('global.messages.validate.newpassword.required'));
    !this.$v.registerAccount.password.minLength && errors.push(this.$t('global.messages.validate.newpassword.minlength'));
    !this.$v.registerAccount.password.maxLength && errors.push(this.$t('global.messages.validate.newpassword.maxlength'));

    return errors;
  }

  public confirmPasswordErrors() {
    const errors = [];
    if (!this.$v.registerAccount.confirmPassword.$dirty) {
      return errors;
    }
    !this.$v.registerAccount.confirmPassword.sameAsPassword && errors.push(this.$t('global.messages.validate.confirmpassword.sameas'));

    return errors;
  }


  public authenticateSession() {
    const exp = localStorage.getItem('exp');
    if (!utils.checkTokenExpValidity(exp)
      && (parseInt(localStorage.getItem('reauthCount')) <= 5
        || !localStorage.getItem('reauthCount'))) {
      // reauthenticate method if current session timedout
      store.commit('setReAuthentication', true);
    }
  }
}
